/**
 * Scrollbars (SRS C2, docs/06-UI-SPEC.md §6.7).
 *
 * Always visible on the right and bottom edges, draggable, and sized to the share of
 * the drawn work that is on screen. Styling is Layer 3 — free to change on request.
 */

import { useMemo, useRef } from 'react';

import { useBoardStore } from '../../board/store.js';
import { dragToCanvas, scrollbarState, type AxisScroll } from '../../board/scrollbars.js';

type Axis = 'horizontal' | 'vertical';

interface BarProps {
  readonly axis: Axis;
  readonly scroll: AxisScroll;
  readonly onScroll: (canvasDelta: number) => void;
}

function Bar({ axis, scroll, onScroll }: BarProps) {
  const track = useRef<HTMLDivElement>(null);
  const last = useRef<number | null>(null);
  const horizontal = axis === 'horizontal';

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (last.current === null || !track.current) return;
    const at = horizontal ? event.clientX : event.clientY;
    const box = track.current.getBoundingClientRect();
    onScroll(dragToCanvas(at - last.current, horizontal ? box.width : box.height, scroll));
    last.current = at;
  };

  const thumbPercent = `${(scroll.thumbRatio * 100).toFixed(3)}%`;
  const freePercent = `${(scroll.offsetRatio * (1 - scroll.thumbRatio) * 100).toFixed(3)}%`;

  return (
    <div
      ref={track}
      className={
        horizontal
          ? 'absolute bottom-0 left-0 right-3 h-3 bg-surface-sunken'
          : 'absolute bottom-3 right-0 top-0 w-3 bg-surface-sunken'
      }
      onPointerDown={(event) => {
        last.current = horizontal ? event.clientX : event.clientY;
        event.currentTarget.setPointerCapture(event.pointerId);
      }}
      onPointerMove={onPointerMove}
      onPointerUp={() => {
        last.current = null;
      }}
      onPointerCancel={() => {
        last.current = null;
      }}
    >
      <div
        aria-hidden="true"
        className="absolute rounded-full bg-border-strong"
        style={
          horizontal
            ? { left: freePercent, width: thumbPercent, top: 3, bottom: 3 }
            : { top: freePercent, height: thumbPercent, left: 3, right: 3 }
        }
      />
    </div>
  );
}

export function Scrollbars() {
  const viewport = useBoardStore((state) => state.viewport);
  const contentBounds = useBoardStore((state) => state.contentBounds);
  const contentVersion = useBoardStore((state) => state.contentVersion);
  const panBy = useBoardStore((state) => state.panBy);

  const state = useMemo(
    () => scrollbarState(viewport, contentBounds?.() ?? null),
    // contentVersion is what tells us the drawn area moved; the provider itself is stable.
    [viewport, contentBounds, contentVersion],
  );

  return (
    <>
      <Bar
        axis="horizontal"
        scroll={state.horizontal}
        onScroll={(canvasDelta) => {
          panBy(-canvasDelta * viewport.zoom, 0);
        }}
      />
      <Bar
        axis="vertical"
        scroll={state.vertical}
        onScroll={(canvasDelta) => {
          panBy(0, -canvasDelta * viewport.zoom);
        }}
      />
    </>
  );
}
